import * as am5 from "@amcharts/amcharts5";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5xy from "@amcharts/amcharts5/xy";
import {Component, ElementRef, Inject, Input, NgZone, PLATFORM_ID, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {gauge} from "../../../models/gauge";
import {ConsumptionService} from "../../../services/consumption.service";

@Component({
    selector: 'app-gauge',
    templateUrl: './gauge.component.html',
    styleUrls: ['./gauge.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GaugeComponent {
  private root: am5.Root;
  private axisDataItem;
  private xAxis;
  private label;
  @Input() public gauge: gauge;
  @ViewChild('gaugeContainer')
  gaugeContainer: ElementRef;

  constructor(@Inject(PLATFORM_ID) private platformId, private zone: NgZone, private consumptionService: ConsumptionService) {
  }
  // Run the function only in the browser
  browserOnly(f: (v) => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular((v) => {
        f(v);
      });
    }
  }

  ngAfterViewInit() {
    this.integrateGauge(this.gauge.diff_percent);
  }

  integrateGauge(value){
    this.browserOnly((v = value) => {

      /* Chart code */
      // Create root element
      // https://www.amcharts.com/docs/v5/getting-started/#Root_element
      const root = am5.Root.new("chartdiv");


      // Set themes
      // https://www.amcharts.com/docs/v5/concepts/themes/
      root.setThemes([
        am5themes_Animated.new(root)
      ]);


      // Create chart
      // https://www.amcharts.com/docs/v5/charts/radar-chart/
      const chart = root.container.children.push(am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: -180,
        endAngle: 0
      }));


      // Create axis and its renderer
      // https://www.amcharts.com/docs/v5/charts/radar-chart/gauge-charts/#Axes
      const axisRenderer = am5radar.AxisRendererCircular.new(root, {
        innerRadius: -40
      });

      axisRenderer.grid.template.setAll({
        stroke: root.interfaceColors.get("background"),
        visible: true,
        strokeOpacity: 0.8
      });

      const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        min: -100,
        max: 100,
        strictMinMax: false,
        renderer: axisRenderer
      }));


      // Add clock hand
      // https://www.amcharts.com/docs/v5/charts/radar-chart/gauge-charts/#Clock_hands
      const axisDataItem = xAxis.makeDataItem({});

      const clockHand = am5radar.ClockHand.new(root, {
        pinRadius: am5.percent(20),
        radius: am5.percent(100),
        bottomWidth: 25
      })

      const bullet = axisDataItem.set("bullet", am5xy.AxisBullet.new(root, {
        sprite: clockHand
      }));

      xAxis.createAxisRange(axisDataItem);

      const label = this.label = chart.radarContainer.children.push(am5.Label.new(root, {
        fill: am5.color(0xffffff),
        centerX: am5.percent(50),
        textAlign: "center",
        centerY: am5.percent(50),
        fontSize: this.getFontSize()
      }));

      axisDataItem.set("value", v);
      let realValue;

      this.consumptionService.consumptions
        .subscribe(
          (data) =>{
            let realValue = data.gauge.diff_percent;
            const displayedValue = data.gauge.diff_percent;

            if(data.gauge.diff_percent > 100 || data.gauge.diff_percent < -100 ){
              realValue = data.gauge.diff_percent;
              const displayedValue = 100;
            }

            axisDataItem.animate({
              key: "value",
              to: displayedValue,
              duration: 800,
              easing: am5.ease.out(am5.ease.cubic)
            });

            bullet.get("sprite").on("rotation", function () {
              const value = axisDataItem.get("value");
              const text = Math.round(axisDataItem.get("value")).toString();
              let fill = am5.color(0x000000);
              xAxis.axisRanges.each(function (axisRange) {
                if (value <= -100 || value >= 100) {
                  axisDataItem.set("value", 100);
                }
                if (value >= axisRange.get("value") && value <= axisRange.get("endValue")) {
                  fill = axisRange.get("axisFill").get("fill");
                }
              })
              label.set("text", Math.round(realValue).toString()+' %');
              clockHand.pin.animate({key: "fill", to: fill, duration: 500, easing: am5.ease.out(am5.ease.cubic)})
              clockHand.hand.animate({key: "fill", to: fill, duration: 500, easing: am5.ease.out(am5.ease.cubic)})
            });
          });

      chart.bulletsContainer.set("mask", undefined);


      // Create axis ranges bands
      // https://www.amcharts.com/docs/v5/charts/radar-chart/gauge-charts/#Bands
      const bandsData = [ {
        title: "",
        color: "#0f9747",
        lowScore: -100,
        highScore: -60
      }, {
        title: "",
        color: "#54b947",
        lowScore: -60,
        highScore: -20
      }, {
        title: "",
        color: "#f3eb0c",
        lowScore: -20,
        highScore: 20
      }, {
        title: "",
        color: "#fdae19",
        lowScore: 20,
        highScore: 60
      }, {
        title: "",
        color: "#f04922",
        lowScore: 60,
        highScore: 100
      }];

      am5.array.each(bandsData, function (data) {
        const axisRange = xAxis.createAxisRange(xAxis.makeDataItem({}));

        axisRange.setAll({
          value: data.lowScore,
          endValue: data.highScore
        });

        axisRange.get("axisFill").setAll({
          visible: true,
          fill: am5.color(data.color),
          fillOpacity: 0.8
        });

        axisRange.get("label").setAll({
          text: data.title,
          inside: true,
          fontSize: "0.9em",
          fill: root.interfaceColors.get("background")
        });
      });


// Make stuff animate on load
      chart.appear(1000, 100);
      this.xAxis = xAxis;
      this.axisDataItem = axisDataItem;
      this.root = root;
    });
  }

  private getFontSize(): string {
    const width = this.gaugeContainer.nativeElement.clientWidth;

    if (width > 320) {
      return '1.2em';
    }

    if (width > 280) {
      return '11px';
    }

    return '10px';
  }

  onResize() {
    this.label.set('fontSize', this.getFontSize());
  }
}
